import { type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useLocation, useNavigate, useParams, useRouteLoaderData } from '@remix-run/react';
import { type PostData, PostElement, parsePostsCategory, type TPostElement } from '@shared/dream-components';

import useLikePost from '~/hooks/useActions/useLikePost';
import useIsMobile from '~/hooks/useIsMobile';
import usePostsInsightsV2 from '~/hooks/useLoaders/usePostsInsightsV2';
import useRecommendedPosts from '~/hooks/useLoaders/useRecommendedPosts';
import usePaginatedPosts from '~/hooks/usePaginatedPosts';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { RootLoaderData } from '~/root';
import type { PostLoaderData } from '~/routes/p/$slug';
import type { StaticPageLoaderData } from '~/services/static_page';
import { buildArchivePaginationSearch, resolveArchivePagination } from '~/utils/archivePagination';
import { resolveCollectionHubHref } from '~/utils/collectionHubHref';
import { findTipTapContentNode } from '~/utils/findTipTapContentNode';

import ClientSideRenderer from '../ClientSideRenderer';

interface Props {
  node: TPostElement;
  children: ReactNode | ReactNode[];
}

const getArchiveOffsetPosts = (posts: PostData[], postsOffset: number) => {
  const postsCopy = [...posts];
  const offsetPosts = postsCopy.slice(postsOffset, postsOffset + posts.length);
  return offsetPosts;
};

const calculatePaginationOffset = (postsOffset: number, perPage: number): number => {
  if (!postsOffset || !perPage || postsOffset <= 0 || perPage <= 0) return 0;
  const result = Math.floor(postsOffset / perPage);
  return Number.isNaN(result) ? 0 : result;
};

const calculateAdjustedTotalPages = (totalPages: number, pagesSkipped: number): number => {
  if (!totalPages || totalPages <= 0) return 0;
  if (!pagesSkipped || pagesSkipped <= 0) return totalPages;
  const result = Math.max(totalPages - pagesSkipped, 0);
  return Number.isNaN(result) ? 0 : result;
};

const Post = ({ node, children }: Props) => {
  const isViewportMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const {
    hasFakeData,
    data: { posts: defaultPosts = [], pagination },
    shouldScrollHorizontally,
    postGroupType,
    postsOffset = 0,
    postsCategory,
    postsCategoryMatch,
    postsAudience,
    structureTiptapState,
    paginateWithNumbers,
  } = node.attrs;

  const isTagPage = postGroupType === 'tag_page';
  const isAuthorPage = postGroupType === 'author_page';
  const isRecommendationsBlock = postGroupType === 'recommendations';
  const isArchive = postGroupType === 'archive';
  const { tag } = useParams();
  const authorId = typeof node.attrs.data?.author_id === 'string' ? node.attrs.data.author_id : undefined;

  const perPage = pagination?.per_page || 3;
  const pagesSkippedByOffset = isArchive ? calculatePaginationOffset(postsOffset, perPage) : 0;
  const adjustedTotalPages = calculateAdjustedTotalPages(pagination?.total_pages || 0, pagesSkippedByOffset);

  const hasPostCardActionsNode = useMemo(
    () => !!(structureTiptapState ? findTipTapContentNode(structureTiptapState, 'postCardActions') : undefined),
    [node.attrs.id],
  );

  const tags = useMemo(() => {
    if (isTagPage && tag) {
      return [tag];
    }
    return parsePostsCategory(postsCategory);
  }, [isTagPage, tag, postsCategory]);
  const categoryTagsMatch = !isTagPage && tags.length > 1 && postsCategoryMatch === 'all' ? 'all' : 'any';
  const routeLoaderData = useTypedLoaderData() as PostLoaderData & Partial<StaticPageLoaderData>;
  const { post } = routeLoaderData;
  const isArchiveRoute = routeLoaderData?.pageRequestParams?.pathname === 'archive';
  const { urlPage, useUrlPagination, isCrawlableLoadMore, linksToCollectionHub, shouldOffsetArchivePosts } =
    resolveArchivePagination({
      isArchive,
      isArchiveRoute,
      paginateWithNumbers: Boolean(paginateWithNumbers),
      search: location.search,
    });
  const { site } = useRouteLoaderData<RootLoaderData>('root') || {};
  const publication = site?.publication;
  const { postsInsights, getPostInsights, updatePostLikeCount } = usePostsInsightsV2();

  const {
    posts,
    showLoadMore,
    loadMorePosts,
    isLoading,
    resetSearchState,
    searchQuery,
    isSearching,
    setIsSearching,
    handleDebouncedSearchQuery,
    handleNonDebouncedSearchQuery,
    goToPage,
    currentPage,
    selectedTagFilters,
    handleTagFilterChange,
    contentTagsMatch,
    handleContentTagsMatchChange,
  } = usePaginatedPosts({
    defaultPosts: shouldOffsetArchivePosts ? getArchiveOffsetPosts(defaultPosts, postsOffset) : defaultPosts,
    pagination: pagination || null,
    enabled: hasFakeData === false || isTagPage || isAuthorPage || !isRecommendationsBlock,
    postGroupType,
    hasFakeData: hasFakeData || false,
    tags,
    categoryTagsMatch,
    onPageFetchSuccess: hasPostCardActionsNode ? getPostInsights : undefined,
    pagesSkippedByOffset,
    audience: postsAudience,
    blockId: node.attrs.id,
    authorIds: authorId ? [authorId] : undefined,
  });

  const recommendedPosts = useRecommendedPosts(post?.id, isRecommendationsBlock);
  const recommendedPostsAsPosts = useMemo(
    () =>
      recommendedPosts?.map(
        (recommendedPost) =>
          ({
            id: recommendedPost.id,
            web_title: recommendedPost.web_title,
            web_subtitle: recommendedPost.web_subtitle,
            comments_state: '',
            override_scheduled_at: recommendedPost.override_scheduled_at,
            scheduled_at: '',
            audience: recommendedPost.audience,
            is_premium: recommendedPost.audience === 'premium',
            slug: recommendedPost.slug,
            content_tags: recommendedPost.content_tags,
            image_url: recommendedPost.image_url,
            authors: recommendedPost.authors,
            estimated_reading_time: recommendedPost.estimated_reading_time,
            estimated_reading_time_display: recommendedPost.estimated_reading_time_display,
          }) as PostData,
      ),
    [recommendedPosts],
  );

  // If on post page, exclude that post in the list of posts
  // Use fetched posts if we have pagination or an active search query
  const shouldUseFetchedPosts = !!pagination || !!searchQuery;
  const paginatedPosts = shouldUseFetchedPosts ? posts : defaultPosts;
  const renderablePosts = useUrlPagination && !searchQuery ? defaultPosts : paginatedPosts;
  const filteredPosts = post ? renderablePosts.filter(({ id }) => post.id !== id) : renderablePosts;

  // Filter tags to only those configured in postsCategory
  const availableTags = useMemo(() => {
    if (!publication?.content_tags || categoryTagsMatch === 'all') return [];
    if (!postsCategory || postsCategory === 'all') {
      return publication.content_tags;
    }
    if (tags.length === 0) return publication.content_tags;
    const categorySet = new Set(tags.map((t) => t.toLowerCase()));
    return publication.content_tags.filter((tag) => categorySet.has(tag.display.toLowerCase()));
  }, [publication, postsCategory, tags, categoryTagsMatch]);

  const { mutateLikes } = useLikePost({ onSuccess: updatePostLikeCount });
  const handleClickLike = (postId: string) => {
    mutateLikes(postId);
  };

  const handleClickComment = (postSlug: string) => {
    navigate(`p/${postSlug}#comments`);
  };

  const buildArchivePageUrl = useCallback(
    (page: number) => `${location.pathname}?${buildArchivePaginationSearch(location.search, page)}`,
    [location.pathname, location.search],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (useUrlPagination) {
        navigate(buildArchivePageUrl(page));
        return;
      }
      goToPage(page, searchQuery || '');
    },
    [useUrlPagination, navigate, buildArchivePageUrl, goToPage, searchQuery],
  );

  const hasCrawlableNextPage = isCrawlableLoadMore && urlPage < (pagination?.total_pages || 0);
  const hubHref = linksToCollectionHub ? resolveCollectionHubHref(tags, publication?.content_tags) : undefined;
  const loadMoreHref = hasCrawlableNextPage ? buildArchivePageUrl(urlPage + 1) : hubHref;

  // Don't render recommendations block if there are no recommended posts
  if (isRecommendationsBlock && (!recommendedPostsAsPosts || recommendedPostsAsPosts.length === 0)) {
    return null;
  }

  return (
    <ClientSideRenderer shouldRenderClientSide={Boolean(shouldScrollHorizontally)}>
      <PostElement
        element={{
          ...node,
          attrs: {
            ...node.attrs,
            isViewportMobile,
            data: isRecommendationsBlock
              ? { posts: recommendedPostsAsPosts, postsInsights }
              : {
                  ...node.attrs.data,
                  posts: filteredPosts,
                  postsInsights,
                },
          },
        }}
        isLivePost
        isViewportMobile={isViewportMobile}
        paginateHasMorePosts={showLoadMore}
        paginateFetchMorePosts={() => loadMorePosts(searchQuery || '')}
        paginateCurrentPage={useUrlPagination ? urlPage : currentPage}
        paginateTotalPages={useUrlPagination ? pagination?.total_pages || 0 : adjustedTotalPages}
        paginateOnPageChange={handlePageChange}
        paginateGetPageHref={useUrlPagination ? buildArchivePageUrl : undefined}
        paginateNextPageHref={loadMoreHref}
        isLoading={isLoading}
        isSearching={isSearching}
        setIsSearching={setIsSearching}
        handleNonDebouncedSearchQuery={handleNonDebouncedSearchQuery}
        handleResetSearchState={resetSearchState}
        onSearchQueryChange={(query) => handleDebouncedSearchQuery(query || '')}
        onLikeClick={handleClickLike}
        onCommentClick={handleClickComment}
        publicationContentTags={availableTags}
        selectedTagFilters={selectedTagFilters}
        onTagFilterChange={handleTagFilterChange}
        contentTagsMatch={contentTagsMatch}
        onContentTagsMatchChange={handleContentTagsMatchChange}
        translations={{
          'search.posts.clear_filters': t('search.posts.clear_filters'),
          'search.posts.match_label': t('search.posts.match_label'),
          'search.posts.match_any': t('search.posts.match_any'),
          'search.posts.match_all': t('search.posts.match_all'),
          'search.posts.match_tooltip_any': t('search.posts.match_tooltip_any'),
          'search.posts.match_tooltip_all': t('search.posts.match_tooltip_all'),
          'search.posts.match_aria_label': t('search.posts.match_aria_label'),
          'search.posts.read_post': t('search.posts.read_post'),
        }}
        showCommentsButton={site?.viewable_site_version?.settings?.comments_enabled}
      >
        {children}
      </PostElement>
    </ClientSideRenderer>
  );
};

export default Post;
